
import React from 'react';
import useRegisterStudentCourses from '../../../hooks/useRegisterStudentCourses';
import useCurrentTeacher from '../../../hooks/useCurrentTeacher';
const PurchaseCourses = () => {
  const { teacher } = useCurrentTeacher();
  const { learningItems, loading } = useRegisterStudentCourses(teacher?.id);
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };
  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(price);
  };
  const renderMedia = (item) => {
    if (item.learning_type === 'course') {
      return <img src={item.thumbnail} alt={item.title} className="w-16 h-16 rounded-lg object-cover" />;
    } else if (item.learning_type === 'video' || item.learning_type === 'podcast') {
      return (
        <video className="w-16 h-16 rounded-lg object-cover" controls>
          <source src={item.thumbnail} type="video/mp4" />
        </video>
      );
    } else if (item.learning_type === 'audio') {
      return (
        <audio controls>
          <source src={item.thumbnail} type="audio/mpeg" />
        </audio>
      );
    }
  };

  const renderSkeleton = () => {
    return (
      <tr>
        <td className="px-4 py-4"><div className="w-16 h-16 bg-gray-200 rounded-lg animate-pulse" /></td>
        <td className="px-4 py-4"><div className="h-4 bg-gray-200 rounded animate-pulse w-32" /></td>
        <td className="px-4 py-4"><div className="h-4 bg-gray-200 rounded animate-pulse w-24" /></td>
        <td className="px-4 py-4"><div className="h-4 bg-gray-200 rounded animate-pulse w-20" /></td>
        <td className="px-4 py-4"><div className="h-4 bg-gray-200 rounded animate-pulse w-36" /></td>
        <td className="px-4 py-4"><div className="h-4 bg-gray-200 rounded animate-pulse w-28" /></td>
      </tr>
    );
  };

  return (
    <div className="mt-6 md:mt-0 md:p-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-[#e8f0f7] rounded-lg flex items-center justify-center">
          <svg className="w-5 h-5 text-[#0f6abf]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
          </svg>
        </div>
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Purchased Learning</h1>
          <p className="text-sm text-gray-500 mt-0.5">Manage and access your materials</p>
        </div>
      </div>

      {/* Table Container */}
      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-6 text-sm font-semibold text-gray-700">Media</th>
                <th className="text-left py-3 px-6 text-sm font-semibold text-gray-700">Title</th>
                <th className="text-left py-3 px-6 text-sm font-semibold text-gray-700">Student</th>
                <th className="text-left py-3 px-6 text-sm font-semibold text-gray-700">Price Paid</th>
                <th className="text-left py-3 px-6 text-sm font-semibold text-gray-700">Transaction ID</th>
                <th className="text-left py-3 px-6 text-sm font-semibold text-gray-700">Purchase Date</th>
              </tr>
            </thead>
            <tbody>
              {loading
                ? Array(3).fill(0).map((_, index) => <React.Fragment key={index}>{renderSkeleton()}</React.Fragment>)
                : learningItems.map(item => (
                  <tr key={item.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                    <td className="py-4 px-6">
                      {renderMedia(item)}
                    </td>
                    <td className="py-4 px-6">
                      <div className="text-sm font-medium text-gray-900">{item.title}</div>
                    </td>
                    <td className="py-4 px-6">
                      <div className="text-sm text-gray-700">{item.student}</div>
                    </td>
                    <td className="py-4 px-6">
                      <div className="text-sm font-medium text-green-600">{formatPrice(item.price)}</div>
                    </td>
                    <td className="py-4 px-6">
                      <div className="text-sm text-gray-600 font-mono">{item.order_id}</div>
                    </td>
                    <td className="py-4 px-6">
                      <div className="text-sm text-gray-500">{formatDate(item.created_at)}</div>
                    </td>
                  </tr>
                ))
              }
            </tbody>
          </table>
        </div>
      </div>

      {/* Empty State */}
      {!loading && learningItems.length === 0 && (
        <div className="mt-6 border-2 border-dashed border-gray-300 rounded-2xl p-12 text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-700 mb-2">No purchases yet</h3>
          <p className="text-sm text-gray-500">When students purchase your courses, they'll appear here.</p>
        </div>
      )}
    </div>
  );
};

export default PurchaseCourses;